import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Modal, Tooltip } from '@playerbase/ui';
import {
  DEFAULT_PLAYTEST_RECRUITMENT_BUTTON_LABEL,
  type PlaytestRecruitmentConfig,
  type PlaytestRecruitmentConfigInput,
  type PlaytestRecruitmentFrequency,
} from '@playerbase/types';

import { useShell } from '../../../shell/use-shell.js';
import { fetchDiscordConnectionForGame } from '../../../games/discord-data.js';
import { useDiscordGuildChannels } from '../../../games/use-discord-guild-channels.js';
import {
  fetchPlaytestRecruitmentConfig,
  requestRecruitmentRepost,
  upsertPlaytestRecruitmentConfig,
  type PlaytestRecruitmentRpcError,
} from '../../../games/recruitment-data.js';
import { RecruitmentFormCard } from './recruitment-form-card.js';
import { PairingInstructionsCard } from './pairing-instructions-card.js';
import { PreviewModal } from './preview-modal.js';

interface DraftState {
  title: string;
  description: string;
  buttonLabel: string;
  repostFrequency: PlaytestRecruitmentFrequency;
  channelIds: string[];
  pairingInstructions: string;
}

interface SyncSnapshot {
  draft: DraftState;
  enabled: boolean;
}

function blankDraft(): DraftState {
  return {
    title: '',
    description: '',
    buttonLabel: DEFAULT_PLAYTEST_RECRUITMENT_BUTTON_LABEL,
    repostFrequency: 'never',
    channelIds: [],
    pairingInstructions: '',
  };
}

function draftFromConfig(config: PlaytestRecruitmentConfig): DraftState {
  return {
    title: config.title,
    description: config.description,
    buttonLabel: config.buttonLabel,
    repostFrequency: config.repostFrequency,
    channelIds: [...config.channelIds],
    pairingInstructions: config.pairingInstructions,
  };
}

function draftsEqual(a: DraftState, b: DraftState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Server enforces a 30s cooldown on request_recruitment_repost. Mirror
// it client-side so the button renders a live countdown rather than
// punching the SQL guard.
const REPOST_COOLDOWN_MS = 30_000;

const ERROR_COPY: Partial<Record<PlaytestRecruitmentRpcError, string>> = {
  not_a_member: "You don't have permission to edit this game.",
  title_too_long: 'Title is too long.',
  description_too_long: 'Description is too long.',
  button_label_required: 'A button label is required.',
  button_label_too_long: 'Button label must be 80 characters or fewer.',
  invalid_repost_frequency: 'Pick a valid repost frequency.',
  too_many_channels: 'You can pick at most 10 channels.',
  pairing_instructions_too_long: 'Pairing instructions are too long.',
};

export function PlaytestRecruitmentPage(): JSX.Element {
  const { activeGame } = useShell();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>(blankDraft);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [repostInFlight, setRepostInFlight] = useState(false);
  const [repostError, setRepostError] = useState<string | null>(null);
  // Epoch ms when the cooldown expires. null = no cooldown active. Tick
  // a 1s interval while set so the button label can render the
  // remaining seconds.
  const [repostCooldownEndsAt, setRepostCooldownEndsAt] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const syncedRef = useRef<SyncSnapshot>({ draft: blankDraft(), enabled: false });

  const { channels, loading: channelsLoading } = useDiscordGuildChannels(connectionId);

  useEffect(() => {
    if (activeGame === null) {
      setConnectionId(null);
      return;
    }
    let cancelled = false;
    void fetchDiscordConnectionForGame(activeGame.id).then((conn) => {
      if (cancelled) return;
      setConnectionId(conn?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [activeGame]);

  const reload = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setLoading(false);
      return;
    }
    const config = await fetchPlaytestRecruitmentConfig(activeGame.id);
    const nextDraft = config !== null ? draftFromConfig(config) : blankDraft();
    const nextEnabled = config?.enabled ?? false;
    setDraft(nextDraft);
    setEnabled(nextEnabled);
    syncedRef.current = { draft: nextDraft, enabled: nextEnabled };
    setLoading(false);
  }, [activeGame]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (repostCooldownEndsAt === null) return undefined;
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [repostCooldownEndsAt]);

  useEffect(() => {
    if (repostCooldownEndsAt !== null && nowMs >= repostCooldownEndsAt) {
      setRepostCooldownEndsAt(null);
    }
  }, [nowMs, repostCooldownEndsAt]);

  const repostCooldownSecondsLeft =
    repostCooldownEndsAt !== null
      ? Math.max(0, Math.ceil((repostCooldownEndsAt - nowMs) / 1000))
      : 0;

  const dirty = !draftsEqual(draft, syncedRef.current.draft);

  useEffect(() => {
    if (!dirty) return undefined;
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  async function persist(
    overrides: Partial<{ enabled: boolean }> = {},
  ): Promise<PlaytestRecruitmentConfig | null> {
    if (activeGame === null) return null;
    const input: PlaytestRecruitmentConfigInput = {
      ...draft,
      enabled: overrides.enabled ?? enabled,
    };
    const result = await upsertPlaytestRecruitmentConfig(activeGame.id, input);
    if (!result.ok || result.data === null) {
      const copy =
        result.error !== null && ERROR_COPY[result.error] !== undefined
          ? (ERROR_COPY[result.error] ?? 'Could not save. Please try again.')
          : 'Could not save the recruitment settings. Please try again.';
      throw new Error(copy);
    }
    return result.data;
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await persist();
      if (saved === null) return;
      const nextDraft = draftFromConfig(saved);
      setDraft(nextDraft);
      setEnabled(saved.enabled);
      syncedRef.current = { draft: nextDraft, enabled: saved.enabled };
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEnable(): Promise<void> {
    setLifecycleBusy(true);
    setLifecycleError(null);
    try {
      const saved = await persist({ enabled: true });
      if (saved === null) return;
      const nextDraft = draftFromConfig(saved);
      setDraft(nextDraft);
      setEnabled(true);
      syncedRef.current = { draft: nextDraft, enabled: true };
      setEnableConfirmOpen(false);
    } catch (e) {
      setLifecycleError(
        e instanceof Error ? e.message : 'Could not enable recruitment. Please try again.',
      );
    } finally {
      setLifecycleBusy(false);
    }
  }

  async function handleRepost(): Promise<void> {
    if (activeGame === null) return;
    setRepostInFlight(true);
    setRepostError(null);
    const result = await requestRecruitmentRepost(activeGame.id);
    setRepostInFlight(false);
    if (!result.ok) {
      setRepostError(
        result.error === 'recruitment_cooldown'
          ? 'Wait a moment before reposting again.'
          : result.error === 'recruitment_not_enabled'
            ? 'Enable recruitment first.'
            : 'Could not repost the recruitment message. Please try again.',
      );
      return;
    }
    // Hold the disabled state for the full cooldown window. Server is
    // still authoritative; this just keeps double-clicks off the wire.
    setRepostCooldownEndsAt(Date.now() + REPOST_COOLDOWN_MS);
    setNowMs(Date.now());
  }

  async function handleDisable(): Promise<void> {
    setLifecycleBusy(true);
    setLifecycleError(null);
    try {
      const saved = await persist({ enabled: false });
      if (saved === null) return;
      const nextDraft = draftFromConfig(saved);
      setDraft(nextDraft);
      setEnabled(false);
      syncedRef.current = { draft: nextDraft, enabled: false };
      setDisableConfirmOpen(false);
    } catch (e) {
      setLifecycleError(
        e instanceof Error ? e.message : 'Could not disable recruitment. Please try again.',
      );
    } finally {
      setLifecycleBusy(false);
    }
  }

  if (activeGame === null) {
    return <div className="px-2 py-6 text-sm text-text-muted">No game selected.</div>;
  }
  if (loading) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading...</div>;
  }

  function update(patch: Partial<DraftState>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  // Readiness gating for Enable: every input that the bot needs at post
  // time has to be filled in. Channels list must be non-empty.
  const enableBlockReason = ((): string | null => {
    if (dirty) return 'Save your changes first.';
    if (connectionId === null) return 'Connect this game to Discord first.';
    if (draft.title.trim() === '') return 'Add a title.';
    if (draft.description.trim() === '') return 'Add a description.';
    if (draft.buttonLabel.trim() === '') return 'Add a button label.';
    if (draft.channelIds.length === 0) return 'Pick at least one channel.';
    if (draft.pairingInstructions.trim() === '') return 'Add pairing instructions.';
    return null;
  })();

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="font-display text-2xl font-bold text-text">
              Recruitment
            </h1>
            <p className="text-sm text-text-muted">
              Configure how players register as testers. Once a player
              pairs, they stay paired for this game across every playtest
              you run.
            </p>
          </div>
          <span
            className="rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-text-muted"
            aria-label={enabled ? 'Status: Enabled' : 'Status: Disabled'}
          >
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>

        {saveError !== null ? <Alert tone="danger">{saveError}</Alert> : null}
        {enabled ? (
          <Alert tone="info">
            Recruitment is live. Disable it before making changes.
          </Alert>
        ) : null}

        {/* Lock editing while live - mirrors the feedback-form builder.
            The Disable action sits in the sticky footer; Save is hidden
            because the surface is read-only. */}
        <div
          className={enabled ? 'pointer-events-none select-none opacity-60' : undefined}
          aria-disabled={enabled}
        >
          <div className="flex flex-col gap-5">
            <RecruitmentFormCard
              title={draft.title}
              description={draft.description}
              buttonLabel={draft.buttonLabel}
              repostFrequency={draft.repostFrequency}
              channelIds={draft.channelIds}
              channels={channels}
              channelsLoading={channelsLoading}
              hasConnection={connectionId !== null}
              onChange={update}
            />

            <PairingInstructionsCard
              value={draft.pairingInstructions}
              onChange={(next) => update({ pairingInstructions: next })}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 mt-auto border-t border-border bg-surface">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-6 py-3">
          <span className="text-xs text-text-muted">
            {enabled ? 'Live - read-only' : dirty ? 'Unsaved changes' : 'Up to date'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" type="button" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
            {enabled ? null : (
              <Button
                variant="primary"
                type="button"
                onClick={() => void handleSave()}
                disabled={saving || !dirty}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {enabled ? (
              <>
                {repostError !== null ? (
                  <span className="text-xs text-danger-text">{repostError}</span>
                ) : null}
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => void handleRepost()}
                  disabled={repostInFlight || repostCooldownEndsAt !== null}
                >
                  {repostInFlight
                    ? 'Posting...'
                    : repostCooldownEndsAt !== null
                      ? `Wait ${repostCooldownSecondsLeft}s...`
                      : 'Repost now'}
                </Button>
                <Button
                  variant="danger"
                  type="button"
                  onClick={() => {
                    setLifecycleError(null);
                    setDisableConfirmOpen(true);
                  }}
                >
                  Disable
                </Button>
              </>
            ) : (
              <Tooltip content={enableBlockReason}>
                <Button
                  variant="primary"
                  type="button"
                  disabled={enableBlockReason !== null}
                  aria-disabled={enableBlockReason !== null}
                  onClick={() => {
                    setLifecycleError(null);
                    setEnableConfirmOpen(true);
                  }}
                >
                  Enable
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={enableConfirmOpen}
        onClose={() => {
          if (!lifecycleBusy) setEnableConfirmOpen(false);
        }}
        labelledById="enable-recruitment-title"
        size="sm"
        busy={lifecycleBusy}
      >
        <Card>
          <Card.Header>
            <Card.Title id="enable-recruitment-title">Enable recruitment?</Card.Title>
            <Card.Description>
              The bot will post your recruitment message in the channels you
              picked. Players who tap the button receive a DM with their
              pairing code.
            </Card.Description>
          </Card.Header>
          {lifecycleError !== null ? (
            <Card.Content>
              <p className="text-sm text-danger-text">{lifecycleError}</p>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => setEnableConfirmOpen(false)}
              disabled={lifecycleBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleEnable()}
              disabled={lifecycleBusy}
            >
              {lifecycleBusy ? 'Enabling...' : 'Enable'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      <Modal
        open={disableConfirmOpen}
        onClose={() => {
          if (!lifecycleBusy) setDisableConfirmOpen(false);
        }}
        labelledById="disable-recruitment-title"
        size="sm"
        busy={lifecycleBusy}
      >
        <Card>
          <Card.Header>
            <Card.Title id="disable-recruitment-title">Disable recruitment?</Card.Title>
            <Card.Description>
              The bot will stop posting and reposting your recruitment
              message. Players already paired stay paired - this only
              affects future sign-ups.
            </Card.Description>
          </Card.Header>
          {lifecycleError !== null ? (
            <Card.Content>
              <p className="text-sm text-danger-text">{lifecycleError}</p>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => setDisableConfirmOpen(false)}
              disabled={lifecycleBusy}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleDisable()}
              disabled={lifecycleBusy}
            >
              {lifecycleBusy ? 'Disabling...' : 'Disable'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={draft.title}
        description={draft.description}
        buttonLabel={draft.buttonLabel}
      />
    </div>
  );
}
